import { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import notification from "antd/lib/notification";
import { AppContext } from "../../context";
import { MEETING_PAYLOAD } from "../../data-mocks";

const STATE_IDLE = "STATE_IDLE";
// const STATE_CREATING = "STATE_CREATING";
// const STATE_JOINING = "STATE_JOINING";
export const STATE_JOINED = "STATE_JOINED";
const STATE_LEAVING = "STATE_LEAVING";
const STATE_ERROR = "STATE_ERROR";
const STATE_HAIRCHECK = "STATE_HAIRCHECK";

export const useDailyEvents = () => {
  const { setMeetingPayload, meetingPayload } = useContext(AppContext);
  const [appState, setAppState] = useState(STATE_IDLE);
  const [roomUrl, setRoomUrl] = useState(null);
  const [callObject, setCallObject] = useState(meetingPayload.callObject);
  const [apiError, setApiError] = useState(false);
  const {
    first_name,
    last_name,
    profile: avatar,
  } = useSelector((state) => state.user.userData) || {};
  const fullName = `${first_name} ${last_name}`;
  const showCall = !apiError && [STATE_JOINED].includes(appState);

  /**
   * Update app state based on reported meeting state changes.
   *
   * NOTE: Here we're showing how to completely clean up a call with destroy().
   * This isn't strictly necessary between join()s, but is good practice when
   * you know you'll be done with the call object for a while, and you're no
   * longer listening to its events.
   */
  useEffect(() => {
    if (!callObject) return;

    const events = ["joined-meeting", "left-meeting", "error", "camera-error"];

    function handleNewMeetingState() {
      switch (callObject.meetingState()) {
        case "joined-meeting":
          setAppState(STATE_JOINED);
          break;
        case "left-meeting":
          callObject.destroy().then(() => {
            setRoomUrl(null);
            setCallObject(null);
            setAppState(STATE_IDLE);
          });
          break;
        case "error":
          setAppState(STATE_ERROR);
          break;
        default:
          break;
      }
    }

    // Use initial state
    handleNewMeetingState();

    /*
     * Listen for changes in state.
     * We can't use the useDailyEvent hook (https://docs.daily.co/reference/daily-react-hooks/use-daily-event) for this
     * because right now, we're not inside a <DailyProvider/> (https://docs.daily.co/reference/daily-react-hooks/daily-provider)
     * context yet. We can't access the call object via daily-react-hooks just yet, but we will later in Call.js and HairCheck.js!
     */
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    // Stop listening for changes in state
    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
      // startLeavingCall();
    };
  }, [callObject]);

  /**
   * We've created a room, so let's start the hair check. We won't be joining the call yet.
   */
  const startHairCheck = useCallback(
    async (url, token, meetingInfo) => {
      if (!token || !url) {
        notification.error({
          message: "Can't join this meeting. kindly report to admin",
        });
        return;
      }
      // // if (appState !== STATE_IDLE) {
      // //   notification.warn({
      // //     message: "You cannot join 2 meetings at the same time",
      // //   });
      // //   return;
      // // }
      // const newCallObject = DailyIframe.createCallObject({
      //   token,
      //   userData: { name: fullName, avatar: avatar },
      // });
      // setRoomUrl(url);
      // try {
      //   setCallObject(newCallObject);

      setMeetingPayload({
        ...meetingPayload,
        // callObject: newCallObject,
        roomUrl: url,
        meetingInfo,
        token,
      });
      //   setAppState(STATE_HAIRCHECK);
      //   await newCallObject.preAuth({ url }); // add a meeting token here if your room is private
      //   await newCallObject.startCamera();
      // } catch (error) {
      //   console.log(error);
      //   notification.error({
      //     message: error.errorMsg || error, // || "Can't join this meeting. kindly report to admin",
      //   });
      // }
    },
    [meetingPayload]
  );

  /**
   * Once we pass the hair check, we can actually join the call.
   */
  const joinCall = useCallback(
    async (meetingPayload_, callFrame) => {
      const { roomUrl, token } = meetingPayload_ || meetingPayload;
      if (token && roomUrl && callFrame) {
        callFrame?.join({
          url: roomUrl,
          token,
          userData: { name: fullName, avatar: avatar },
        });
      } else {
        console.log("no token or url");
      }
    },
    [callObject, roomUrl, meetingPayload]
  );

  /**
   * Start leaving the current call.
   */
  const startLeavingCall = useCallback(
    (callObject) => {
      if (!callObject) {
        setMeetingPayload(MEETING_PAYLOAD);
        return;
      }
      // If we're in the error state, we've already "left", so just clean up
      if (appState === STATE_ERROR) {
        callObject.destroy().then(() => {
          setRoomUrl(null);
          setCallObject(null);
          setAppState(STATE_IDLE);
        });
      } else {
        /* This will trigger a `left-meeting` event, which in turn will trigger
      the full clean-up as seen in handleNewMeetingState() below. */
        setAppState(STATE_LEAVING);
        callObject.leave();
        setMeetingPayload(MEETING_PAYLOAD);
      }
    },
    [callObject, appState]
  );

  return {
    startHairCheck,
    joinCall,
    startLeavingCall,
    callObject,
    showCall,
    appState,
  };
};
